// ─── Collage Canvas ───────────────────────────────────────────
// Renders a collage section with absolute-positioned elements.
// Phase 2: Resize handles, rotation handle, snap guides.

import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import CollageElementRenderer from './CollageElementRenderer';
import { CollageEditContext } from './CollageEditContext';
import { getRenderableElements } from '../../lib/utils/collageIntent';
import { getElementFrameStyle } from '../../lib/utils/collageLayout';
import ScrollEntrance, { ANIMATIONS } from './ScrollEntrance';
import EditorGuides from './EditorGuides';
import { resolveSectionLayout } from '../../lib/utils/sectionLayout';

function BackgroundLayer({ background, globalBgActive }) {
  if (!background || globalBgActive) return null;
  if (background.type === 'color') {
    return <div className="absolute inset-0" style={{ background: background.color }} />;
  }
  if (background.type === 'image') {
    return (
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `${
              background.overlay
                ? `linear-gradient(${background.overlay}, ${background.overlay}), `
                : ''
            }url(${background.src})`,
            filter: `grayscale(${background.grayscale || 0})`,
            transform: 'scale(1.03)',
          }}
        />
      </div>
    );
  }
  return null;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

const SNAP_THRESHOLD = 0.015;
const GRID_DIVISIONS = 12; // 12-column grid

function snapValue(val, targets) {
  for (const t of targets) {
    if (Math.abs(val - t) < SNAP_THRESHOLD) return { value: t, snapped: true };
  }
  return { value: val, snapped: false };
}

function snapToGrid(val, gridSize) {
  const rounded = Math.round(val / gridSize) * gridSize;
  if (Math.abs(val - rounded) < SNAP_THRESHOLD * 1.2) return { value: rounded, snapped: true };
  return { value: val, snapped: false };
}

// ─── Snap Guide Lines ─────────────────────────────────────────
function SnapGuides({ guides }) {
  if (!guides) return null;
  return (
    <>
      {guides.vertical !== null && (
        <div
          className="absolute top-0 bottom-0 pointer-events-none"
          style={{
            left: `${guides.vertical * 100}%`,
            width: '1px',
            background: 'rgba(59,130,246,0.5)',
            zIndex: 999,
          }}
        />
      )}
      {guides.horizontal !== null && (
        <div
          className="absolute left-0 right-0 pointer-events-none"
          style={{
            top: `${guides.horizontal * 100}%`,
            height: '1px',
            background: 'rgba(59,130,246,0.5)',
            zIndex: 999,
          }}
        />
      )}
    </>
  );
}

// ─── Grid Overlay ─────────────────────────────────────────────
function GridOverlay() {
  const step = 1 / GRID_DIVISIONS;
  const lines = [];
  for (let i = 1; i < GRID_DIVISIONS; i++) {
    const pos = i * step;
    lines.push(
      <div key={`v${i}`} className="absolute top-0 bottom-0 pointer-events-none" style={{
        left: `${pos * 100}%`, width: '1px',
        background: i === GRID_DIVISIONS / 2 ? 'rgba(59,130,246,0.15)' : 'rgba(0,0,0,0.06)',
      }} />,
      <div key={`h${i}`} className="absolute left-0 right-0 pointer-events-none" style={{
        top: `${pos * 100}%`, height: '1px',
        background: i === GRID_DIVISIONS / 2 ? 'rgba(59,130,246,0.15)' : 'rgba(0,0,0,0.06)',
      }} />
    );
  }
  return <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 998 }}>{lines}</div>;
}

// ─── Interactive Element Wrapper ──────────────────────────────
// Wraps actual content and handles move, resize (corners), and rotate (top knob).
// Unlike a separate overlay, this wrapper encloses the real content so it has
// natural height — clicking anywhere on the visible element starts a drag.
function InteractiveElementWrapper({ element, isMobile, sectionId, containerRef, editCtx, setGuides, children }) {
  const isSelected = editCtx.selectedElement?.sectionId === sectionId &&
                     editCtx.selectedElement?.elementId === element.id;
  // Phase 1.9 — additionally show a selection ring when this element is in
  // the multi-select set (even if it isn't the "primary" single selection).
  const isInMultiSelect = Array.isArray(editCtx.selectionSet)
    ? editCtx.selectionSet.some(
        (s) => s.sectionId === sectionId && s.elementId === element.id,
      )
    : false;
  const isEditing = editCtx.editingElement?.sectionId === sectionId &&
                    editCtx.editingElement?.elementId === element.id;
  const isTextElement = element.type === 'text_block' || element.type === 'title_block' || element.type === 'text_note';
  const layoutKey = isMobile ? 'mobile' : 'desktop';
  const frameStyle = getElementFrameStyle(element.layout, isMobile);

  // ── Click to select ──
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (isEditing) return;
    // Phase 1.9 — Shift+click toggles multi-select (add/remove from set).
    if (e.shiftKey && editCtx.onToggleSelection) {
      editCtx.onToggleSelection(sectionId, element.id);
      return;
    }
    editCtx.onSelectElement(sectionId, element.id);
  }, [editCtx, sectionId, element.id, isEditing]);

  // ── Double-click to edit text inline ──
  const handleDoubleClick = useCallback((e) => {
    e.stopPropagation();
    if (isTextElement && editCtx.onEditElement) {
      editCtx.onEditElement(sectionId, element.id);
    }
  }, [editCtx, sectionId, element.id, isTextElement]);

  // ── Move interaction (manual drag like floor plan) ──
  const handleMoveStart = useCallback((e) => {
    // Phase 1.9 — Shift+click is reserved for multi-select toggling.
    // Don't start a drag; let onClick handle the selection toggle.
    if (e.shiftKey) {
      return;
    }
    // Phase 1.8 — locked elements can be selected but not moved.
    if (element.locked) {
      editCtx.onSelectElement(sectionId, element.id);
      return;
    }
    // Don't start drag from resize/rotate handles
    if (e.target.dataset.handleType) return;
    e.preventDefault();
    e.stopPropagation();
    editCtx.onSelectElement(sectionId, element.id);

    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();

    const currentLayout = element.layout?.[layoutKey] || element.layout?.desktop || {};
    const startMX = e.clientX;
    const startMY = e.clientY;
    const startX = currentLayout.x ?? 0.5;
    const startY = currentLayout.y ?? 0.5;
    const useGrid = editCtx.gridEnabled;
    const gridStep = 1 / GRID_DIVISIONS;

    const onMouseMove = (moveE) => {
      const dx = (moveE.clientX - startMX) / rect.width;
      const dy = (moveE.clientY - startMY) / rect.height;

      let x = clamp(startX + dx, 0.04, 0.96);
      let y = clamp(startY + dy, 0.04, 0.96);

      let guidesH = null, guidesV = null;

      if (useGrid) {
        const gx = snapToGrid(x, gridStep);
        const gy = snapToGrid(y, gridStep);
        if (gx.snapped) { x = gx.value; guidesV = gx.value; }
        if (gy.snapped) { y = gy.value; guidesH = gy.value; }
      } else {
        const sx = snapValue(x, [0.5, 0.25, 0.75]);
        const sy = snapValue(y, [0.5, 0.25, 0.75]);
        if (sx.snapped) { x = sx.value; guidesV = sx.value; }
        if (sy.snapped) { y = sy.value; guidesH = sy.value; }
      }

      setGuides({ vertical: guidesV, horizontal: guidesH });
      editCtx.onElementMove(sectionId, element.id, x, y);
    };

    const onMouseUp = () => {
      setGuides(null);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [editCtx, sectionId, element, containerRef, layoutKey, setGuides]);

  // ── Resize interaction (corner handles) ──
  const handleResizeStart = useCallback((corner) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const currentLayout = element.layout?.[layoutKey] || element.layout?.desktop || {};
    const startW = currentLayout.w || 0.3;
    const startMouseX = e.clientX;

    const isLeft = corner === 'tl' || corner === 'bl';

    const onMouseMove = (moveE) => {
      const deltaX = (moveE.clientX - startMouseX) / rect.width;
      const scaledDelta = isLeft ? -deltaX : deltaX;
      const newW = clamp(startW + scaledDelta * 2, 0.06, 1.0);
      editCtx.onElementResize?.(sectionId, element.id, newW);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [editCtx, sectionId, element, containerRef, layoutKey]);

  // ── Rotate interaction ──
  const handleRotateStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const currentLayout = element.layout?.[layoutKey] || element.layout?.desktop || {};
    const centerX = rect.left + (currentLayout.x ?? 0.5) * rect.width;
    const centerY = rect.top + (currentLayout.y ?? 0.5) * rect.height;

    const onMouseMove = (moveE) => {
      const angle = Math.atan2(moveE.clientX - centerX, -(moveE.clientY - centerY)) * (180 / Math.PI);
      let snapped = Math.round(angle);
      for (const snap of [0, 45, 90, -45, -90, 180, -180]) {
        if (Math.abs(snapped - snap) < 4) { snapped = snap; break; }
      }
      snapped = clamp(snapped, -180, 180);
      editCtx.onElementRotate?.(sectionId, element.id, snapped);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [editCtx, sectionId, element, containerRef, layoutKey]);

  const cursorMap = { tl: 'nwse-resize', tr: 'nesw-resize', bl: 'nesw-resize', br: 'nwse-resize' };

  return (
    <div
      style={{
        ...frameStyle,
        rotate: `${element.rotation || 0}deg`,
        cursor: isEditing ? 'text' : element.locked ? 'not-allowed' : isSelected ? 'grab' : 'pointer',
        zIndex: isSelected ? 50 : (frameStyle.zIndex || 1),
      }}
      className="group"
      onMouseDown={isEditing ? undefined : handleMoveStart}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      data-testid={`drag-handle-${element.id}`}
      data-locked={element.locked ? 'true' : undefined}
    >
      {/* The actual rendered content — clone with editing props */}
      {isEditing ? React.cloneElement(children, {
        isEditing: true,
        onEditText: (field, value) => editCtx.onEditText(sectionId, element.id, field, value),
      }) : children}

      {/* Selection ring — matches the content's natural size */}
      <div className={`absolute inset-0 rounded-md border-2 transition-all pointer-events-none ${
        isSelected
          ? element.locked
            ? 'border-amber-400/70 bg-amber-400/5'
            : 'border-blue-500 bg-blue-500/5'
          : isInMultiSelect
            ? 'border-blue-400/80 bg-blue-400/5'
            : 'border-transparent group-hover:border-blue-400/40'
      }`} />

      {isSelected && !element.locked && (
        <>
          {/* ── Resize corner handles ── */}
          {['tl', 'tr', 'bl', 'br'].map(corner => (
            <div
              key={corner}
              data-handle-type="resize"
              className="absolute w-3 h-3 bg-white border-2 border-blue-500 rounded-sm shadow-md"
              style={{
                top: corner[0] === 't' ? -6 : undefined,
                bottom: corner[0] === 'b' ? -6 : undefined,
                left: corner[1] === 'l' ? -6 : undefined,
                right: corner[1] === 'r' ? -6 : undefined,
                cursor: cursorMap[corner],
                zIndex: 10,
              }}
              onMouseDown={handleResizeStart(corner)}
              data-testid={`resize-${corner}-${element.id}`}
            />
          ))}

          {/* ── Rotation handle ── */}
          <div
            className="absolute left-1/2 flex flex-col items-center"
            style={{ top: -28, transform: 'translateX(-50%)', zIndex: 10 }}
          >
            <div
              data-handle-type="rotate"
              className="w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-md cursor-grab hover:bg-blue-50 transition-colors"
              onMouseDown={handleRotateStart}
              data-testid={`rotate-handle-${element.id}`}
            />
            <div className="w-px h-2.5 bg-blue-400 pointer-events-none" />
          </div>
        </>
      )}

      {!isSelected && (
        <>
          {/* Hover dot indicators */}
          <div className="absolute -top-1.5 -left-1.5 w-3 h-3 rounded-full border-2 border-white shadow-md bg-gray-400 opacity-0 group-hover:opacity-70 transition-opacity pointer-events-none" />
          <div className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full border-2 border-white shadow-md bg-gray-400 opacity-0 group-hover:opacity-70 transition-opacity pointer-events-none" />
          <div className="absolute -bottom-1.5 -left-1.5 w-3 h-3 rounded-full border-2 border-white shadow-md bg-gray-400 opacity-0 group-hover:opacity-70 transition-opacity pointer-events-none" />
          <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 rounded-full border-2 border-white shadow-md bg-gray-400 opacity-0 group-hover:opacity-70 transition-opacity pointer-events-none" />
        </>
      )}
    </div>
  );
}


// ─── Animation Preview Wrapper ────────────────────────────────
// During preview mode, applies the element's entrance animation with staggered timing.
function AnimationPreviewWrapper({ element, index, active, children }) {
  const entrance = element.entrance || 'none';
  const anim = ANIMATIONS[entrance];

  if (!active || entrance === 'none' || !anim) return children;

  const baseDelay = (element.entranceDelay || 0) * 1000;
  const stagger = index * 120;
  const totalDelay = baseDelay + stagger;

  // Text effects preview as fade (real effect plays on public page)
  const animName = anim.isTextEffect ? 'previewEntrance-fade' : `previewEntrance-${entrance}`;

  return (
    <div
      style={{
        animation: `${animName} 0.7s cubic-bezier(0.22,1,0.36,1) ${totalDelay}ms both`,
      }}
    >
      <style>{`
        @keyframes previewEntrance-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes previewEntrance-slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes previewEntrance-slide-down {
          from { opacity: 0; transform: translateY(-40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes previewEntrance-slide-left {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes previewEntrance-slide-right {
          from { opacity: 0; transform: translateX(-40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes previewEntrance-zoom {
          from { opacity: 0; transform: scale(0.85); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      {children}
    </div>
  );
}

// ─── Main Canvas ──────────────────────────────────────────────
export default function CollageCanvas({ section, data, theme, settings, rsvpRef, globalBgActive, inlineWidgets }) {
  const windowIsMobile = useIsMobile();
  const editCtx = useContext(CollageEditContext);
  const containerRef = useRef(null);
  const [snapGuides, setSnapGuides] = useState(null);

  // In editor: respect layoutMode from context. On public page: use window width.
  const isMobile = editCtx?.layoutMode
    ? editCtx.layoutMode === 'mobile'
    : windowIsMobile;

  const baseElements = data?._elements || section.elements || [];
  // Canonical section layout — shared with public SectionV1CollageCanvas.
  // CRITICAL: editor MUST use aspect-ratio CSS (not fixed pixel height) to
  // match public rendering. Without this, editor at 374px wide renders the
  // section at 374×4100 (aspect 10.96) while public at 430px wide renders
  // 430×4521 (aspect 10.51). Same yPercent then resolves to different
  // proportional positions → visible vertical drift.
  const sectionLayout = resolveSectionLayout(section, data, isMobile);

  const mergedElements = baseElements.map(el => {
    const overrides = data?.[el.id] || {};
    // Deep merge layout so partial overrides don't erase desktop/mobile keys
    const mergedLayout = overrides.layout ? {
      ...el.layout,
      ...overrides.layout,
      desktop: { ...(el.layout?.desktop || {}), ...(overrides.layout?.desktop || {}) },
      mobile: { ...(el.layout?.mobile || {}), ...(overrides.layout?.mobile || {}) },
    } : el.layout;
    return { ...el, ...overrides, layout: mergedLayout };
  });

  const renderableElements = getRenderableElements(mergedElements, isMobile);

  // Deselect on canvas background click (also clears multi-select)
  const handleCanvasClick = useCallback(() => {
    if (!editCtx) return;
    if (editCtx.onClearSelection) {
      editCtx.onClearSelection();
    } else {
      editCtx.onSelectElement(null, null);
    }
  }, [editCtx]);

  // ── Keyboard nudging ──
  useEffect(() => {
    if (!editCtx) return;

    const handleKeyDown = (e) => {
      // Don't capture when typing in inputs or contentEditable
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (e.target.isContentEditable) return;

      // Phase 1.9 — multi-select group nudge takes priority when 2+ selected.
      const multi = Array.isArray(editCtx.selectionSet) ? editCtx.selectionSet : [];
      const hasMulti = multi.length >= 2;

      const sel = editCtx.selectedElement;
      if (!hasMulti) {
        if (!sel || sel.sectionId !== section.id) return;
        // Skip if inline editing is active
        if (editCtx.editingElement?.sectionId === section.id && editCtx.editingElement?.elementId === sel.elementId) return;

        // ── Delete element ──
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          if (editCtx.onDeleteElement) {
            editCtx.onDeleteElement(section.id, sel.elementId);
          }
          return;
        }
      }

      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      e.preventDefault();

      const layoutKey = isMobile ? 'mobile' : 'desktop';

      if (hasMulti) {
        // Phase 1.9 — group nudge: 1% per arrow, 5% with Shift. Locked
        // elements are skipped.
        const step = e.shiftKey ? 0.05 : 0.01;
        let dx = 0, dy = 0;
        if (e.key === 'ArrowUp')    dy = -step;
        if (e.key === 'ArrowDown')  dy =  step;
        if (e.key === 'ArrowLeft')  dx = -step;
        if (e.key === 'ArrowRight') dx =  step;

        // Only nudge elements that belong to THIS section's keyboard handler.
        const inSection = multi.filter((s) => s.sectionId === section.id);
        for (const s of inSection) {
          const el = renderableElements.find((r) => r.id === s.elementId);
          if (!el || el.locked) continue;
          const cur = el.layout?.[layoutKey] || el.layout?.desktop || {};
          const x = clamp((cur.x ?? 0.5) + dx, 0.04, 0.96);
          const y = clamp((cur.y ?? 0.5) + dy, 0.04, 0.96);
          editCtx.onElementMove(section.id, s.elementId, x, y);
        }
        return;
      }

      // Single-select fine/coarse nudge (legacy behavior).
      const step = e.shiftKey ? 0.025 : 0.003;
      let dx = 0, dy = 0;
      if (e.key === 'ArrowUp')    dy = -step;
      if (e.key === 'ArrowDown')  dy =  step;
      if (e.key === 'ArrowLeft')  dx = -step;
      if (e.key === 'ArrowRight') dx =  step;

      // Find the element's current position
      const el = renderableElements.find(el => el.id === sel.elementId);
      if (!el) return;
      // Phase 1.8 — locked elements ignore arrow-key nudges too.
      if (el.locked) return;
      const cur = el.layout?.[layoutKey] || el.layout?.desktop || {};
      const x = clamp((cur.x ?? 0.5) + dx, 0.04, 0.96);
      const y = clamp((cur.y ?? 0.5) + dy, 0.04, 0.96);

      editCtx.onElementMove(section.id, sel.elementId, x, y);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editCtx, section.id, renderableElements, isMobile]);

  // Sort inline widgets by yPosition
  const sortedWidgets = (inlineWidgets || []).slice().sort((a, b) => a.yPosition - b.yPosition);

  return (
    <section
      id={section.id}
      ref={containerRef}
      className="relative w-full"
      style={{
        // Match public SectionV1CollageCanvas: aspect-ratio CSS guarantees
        // identical proportions across editor/public for the same container
        // width. CSS aspect-ratio = width/height; our internal value is
        // height/width, hence "1 / aspectRatio".
        aspectRatio: `1 / ${sectionLayout.aspectRatio}`,
      }}
      data-section-type="collage"
      data-section-height-px={sectionLayout.heightPx}
      data-section-height-source={sectionLayout.source}
      data-testid={`collage-section-${section.id}`}
      onClick={handleCanvasClick}
    >
      {/* Canvas content container — overflow hidden for elements */}
      <div className="absolute inset-0 overflow-hidden">
        <BackgroundLayer background={section.background} globalBgActive={globalBgActive} />

        {/* Grid overlay (edit mode, when grid snapping is enabled) */}
        {editCtx?.gridEnabled && <GridOverlay />}

        {/* Editor guides (Phase 1.8) — center lines + safe-area dashed rect
            + mobile viewport-fold markers. sectionHeightPx uses the
            *mobile* logical height so the 844px reference is meaningful
            in both layout modes. */}
        {editCtx?.guidesEnabled && (
          <EditorGuides
            enabled={editCtx.guidesEnabled}
            sectionHeightPx={sectionLayout.heightPx}
          />
        )}

        {/* Snap guide lines (edit mode only) */}
        {editCtx && <SnapGuides guides={snapGuides} />}

        {/* Elements — in edit mode, wrapped with interactive handlers */}
        <div className="absolute inset-0">
          {renderableElements.map((element, idx) => (
            editCtx ? (
              <AnimationPreviewWrapper
                key={element.id}
                element={element}
                index={idx}
                active={editCtx.animationPreview}
              >
                <InteractiveElementWrapper
                  element={element}
                  isMobile={isMobile}
                  sectionId={section.id}
                  containerRef={containerRef}
                  editCtx={editCtx}
                  setGuides={setSnapGuides}
                >
                  <CollageElementRenderer
                    element={element}
                    isMobile={isMobile}
                    theme={theme}
                    rsvpRef={rsvpRef}
                    skipPositioning
                  />
                </InteractiveElementWrapper>
              </AnimationPreviewWrapper>
            ) : (
              <ScrollEntrance
                key={element.id}
                entrance={element.entrance}
                delay={element.entranceDelay}
              >
                <CollageElementRenderer
                  element={element}
                  isMobile={isMobile}
                  theme={theme}
                  rsvpRef={rsvpRef}
                />
              </ScrollEntrance>
            )
          ))}
        </div>
      </div>

      {/* Inline widgets rendered at their Y position — NOT clipped by overflow */}
      {sortedWidgets.map(widget => {
        return (
          <div
            key={widget.section.id}
            className="absolute left-0 right-0"
            style={{
              top: `${widget.yPosition * 100}%`,
              zIndex: 50,
            }}
            data-testid={`inline-widget-${widget.section.id}`}
          >
            {/* Widget renders inline at this Y position */}
            <InlineWidgetRenderer
              section={widget.section}
              data={widget.data}
              theme={theme}
              settings={settings}
              rsvpRef={rsvpRef}
            />
          </div>
        );
      })}
    </section>
  );
}

// ─── Inline Widget Renderer ───────────────────────────────────
// Renders a widget section inline within the canvas without importing SectionRenderer
// (to avoid circular imports). Uses the same registry approach.
function InlineWidgetRenderer({ section, data, theme, settings, rsvpRef }) {
  // Lazy-import section components to avoid circular dependency
  const [Component, setComponent] = useState(null);

  useEffect(() => {
    const registry = {
      'timeline': () => import('../sections/timeline/TimelineSection'),
      'gallery': () => import('../sections/gallery/GallerySection'),
      'rsvp': () => import('../sections/rsvp/RsvpSection'),
      'event-details': () => import('../sections/details/EventDetailsSection'),
      'venue': () => import('../sections/venue/VenueSection'),
      'countdown': () => import('../sections/countdown/CountdownSection'),
      'slideshow': () => import('../sections/slideshow/SlideshowSection'),
    };

    const loader = registry[section.type];
    if (loader) {
      loader().then(mod => setComponent(() => mod.default));
    }
  }, [section.type]);

  if (!Component) return null;

  return (
    <Component
      section={section}
      data={data}
      theme={theme}
      settings={settings}
      rsvpRef={rsvpRef}
    />
  );
}
